import jsPDF from 'jspdf';

/**
 * ArUco marker generation using the original ArUco dictionary (5x5, 1024 markers).
 * Each row of the 5x5 inner pattern encodes 2 data bits using a Hamming code.
 * The full marker is 7x7 (5x5 inner + 1-cell black border).
 *
 * 0 = black cell, 1 = white cell
 */

// Hamming code words: maps 2-bit data to 5-bit encoded row
const HAMMING_WORDS: number[][] = [
  [1, 0, 0, 0, 0], // data (0,0)
  [1, 0, 1, 1, 1], // data (0,1)
  [0, 1, 0, 0, 1], // data (1,0)
  [0, 1, 1, 1, 0], // data (1,1)
];

// Marker IDs for each corner of the form
export const MARKER_IDS = {
  TOP_LEFT: 0,
  TOP_RIGHT: 1,
  BOTTOM_LEFT: 2,
  BOTTOM_RIGHT: 3,
} as const;

// Layout constants shared between PDF generation and OMR processing
export const FORM_LAYOUT = {
  // Page dimensions (A4 landscape in mm)
  pageWidth: 297,
  pageHeight: 210,
  margin: 10,

  // ArUco marker placement
  markerSize: 10,       // 10mm x 10mm
  markerInset: 5,       // 5mm from edge (top markers)
  bottomMarkerInset: 2, // 2mm from edge (bottom markers, closer to edge)

  // Bubble configuration
  bubbleRadius: 2.5,
  bubbleSpacing: 6.5,
  questionHeight: 7.2,
  questionNumWidth: 8,

  // Name field
  nameFieldHeight: 5,
  nameFieldWidth: 70,

  // Part title height
  titleHeight: 10,
} as const;

/**
 * Generate the 5x5 inner pattern for an ArUco marker.
 * ID is encoded as 10 bits across 5 rows (2 bits per row, MSB first).
 */
export function generateArucoPattern(id: number): number[][] {
  if (id < 0 || id > 1023) throw new Error('ArUco ID must be 0-1023');

  const pattern: number[][] = [];

  for (let row = 0; row < 5; row++) {
    // Row 0 uses bits 9,8 (MSB); Row 4 uses bits 1,0 (LSB)
    const shift = (4 - row) * 2;
    const d1 = (id >> (shift + 1)) & 1;
    const d0 = (id >> shift) & 1;
    const wordIndex = d1 * 2 + d0;
    pattern.push([...HAMMING_WORDS[wordIndex]]);
  }

  return pattern;
}

/**
 * Generate the full 7x7 marker grid (5x5 inner + 1-cell black border).
 * 0 = black, 1 = white
 */
export function generateArucoGrid(id: number): number[][] {
  const inner = generateArucoPattern(id);
  const grid: number[][] = [];

  // Top border row (all black)
  grid.push([0, 0, 0, 0, 0, 0, 0]);

  // Inner rows with black side borders
  for (let row = 0; row < 5; row++) {
    grid.push([0, ...inner[row], 0]);
  }

  // Bottom border row (all black)
  grid.push([0, 0, 0, 0, 0, 0, 0]);

  return grid;
}

/**
 * Draw an ArUco marker on a jsPDF document.
 * Draws each cell of the 7x7 grid as a filled (black) or empty (white) rectangle.
 */
export function drawArucoMarker(
  doc: jsPDF,
  id: number,
  x: number,
  y: number,
  size: number
): void {
  const grid = generateArucoGrid(id);
  const cellSize = size / 7;

  // Draw white background first
  doc.setFillColor(255, 255, 255);
  doc.rect(x, y, size, size, 'F');

  // Draw black cells
  doc.setFillColor(0, 0, 0);
  for (let row = 0; row < 7; row++) {
    for (let col = 0; col < 7; col++) {
      if (grid[row][col] === 0) {
        doc.rect(x + col * cellSize, y + row * cellSize, cellSize, cellSize, 'F');
      }
    }
  }
}

/**
 * Compute the expected marker center positions in mm on the PDF.
 * Returns { TL, TR, BL, BR } each as { x, y }.
 */
export function getMarkerCenters(): { TL: { x: number; y: number }; TR: { x: number; y: number }; BL: { x: number; y: number }; BR: { x: number; y: number } } {
  const { pageWidth, pageHeight, markerSize, markerInset, bottomMarkerInset } = FORM_LAYOUT;
  const halfMarker = markerSize / 2;

  return {
    TL: { x: markerInset + halfMarker, y: markerInset + halfMarker - 2 },
    TR: { x: pageWidth - markerInset - halfMarker, y: markerInset + halfMarker - 2 },
    BL: { x: markerInset + halfMarker, y: pageHeight - bottomMarkerInset - halfMarker },
    BR: { x: pageWidth - markerInset - halfMarker, y: pageHeight - bottomMarkerInset - halfMarker },
  };
}

interface QuestionLayout {
  questionNumber: number;
  partId: number | null;
  bubbles: { x: number; y: number; answerIndex: number; letter: string }[];
}

interface FormLayoutInput {
  questions: { question_number: number; number_of_answers: number; part_id: number | null }[];
  parts: { id: number; titre: string; ordre: number }[];
}

/**
 * Compute the exact positions (in mm) of all bubbles on the form.
 * This replicates the layout algorithm from bubbleFormGenerator.ts.
 */
export function computeBubblePositions(test: FormLayoutInput): QuestionLayout[] {
  const { margin, titleHeight, bubbleRadius, bubbleSpacing, questionHeight, questionNumWidth, markerInset, bottomMarkerInset, markerSize } = FORM_LAYOUT;
  const contentWidth = FORM_LAYOUT.pageWidth - 2 * margin;
  const contentHeight = FORM_LAYOUT.pageHeight - markerInset - bottomMarkerInset - markerSize - 2;

  const layouts: QuestionLayout[] = [];

  // Group questions by part
  const questionsByPart: { [key: string]: typeof test.questions } = {};
  const partsMap: { [key: number]: typeof test.parts[0] } = {};

  test.parts.forEach(part => {
    partsMap[part.id] = part;
    questionsByPart[`part_${part.id}`] = [];
  });
  questionsByPart['no_part'] = [];

  test.questions.forEach(question => {
    if (question.part_id && partsMap[question.part_id]) {
      questionsByPart[`part_${question.part_id}`].push(question);
    } else {
      questionsByPart['no_part'].push(question);
    }
  });

  const sortedParts = [...test.parts].sort((a, b) => a.ordre - b.ordre);

  if (sortedParts.length > 0) {
    const partWidth = contentWidth / sortedParts.length;

    sortedParts.forEach((part, partIndex) => {
      const partQuestions = questionsByPart[`part_${part.id}`] || [];
      if (partQuestions.length === 0) return;

      const x = margin + partIndex * partWidth;
      const y = markerInset + titleHeight;
      const height = contentHeight - titleHeight;

      const maxQuestionsPerColumn = Math.floor(height / questionHeight);
      const optimalColumns = Math.ceil(partQuestions.length / maxQuestionsPerColumn);
      const questionsPerColumn = Math.ceil(partQuestions.length / optimalColumns);
      const columnWidth = partWidth / optimalColumns;

      partQuestions.forEach((question, index) => {
        const column = Math.floor(index / questionsPerColumn);
        const row = index % questionsPerColumn;

        const questionX = x + column * columnWidth;
        const questionY = y + row * questionHeight;
        const verticalCenter = questionY + questionHeight / 2;

        const bubbles: QuestionLayout['bubbles'] = [];
        const bubbleStartX = questionX + questionNumWidth;

        for (let i = 0; i < question.number_of_answers; i++) {
          const bubbleX = bubbleStartX + i * bubbleSpacing + bubbleRadius;
          bubbles.push({
            x: bubbleX,
            y: verticalCenter,
            answerIndex: i,
            letter: String.fromCharCode(65 + i),
          });
        }

        layouts.push({
          questionNumber: question.question_number,
          partId: question.part_id,
          bubbles,
        });
      });
    });
  } else {
    // No parts: all questions in one frame
    const allQuestions = questionsByPart['no_part'] || [];
    if (allQuestions.length > 0) {
      const y = markerInset;
      const height = contentHeight;

      const maxQuestionsPerColumn = Math.floor(height / questionHeight);
      const optimalColumns = Math.ceil(allQuestions.length / maxQuestionsPerColumn);
      const questionsPerColumn = Math.ceil(allQuestions.length / optimalColumns);
      const columnWidth = contentWidth / optimalColumns;

      allQuestions.forEach((question, index) => {
        const column = Math.floor(index / questionsPerColumn);
        const row = index % questionsPerColumn;

        const questionX = margin + column * columnWidth;
        const questionY = y + row * questionHeight;
        const verticalCenter = questionY + questionHeight / 2;

        const bubbles: QuestionLayout['bubbles'] = [];
        const bubbleStartX = questionX + questionNumWidth;

        for (let i = 0; i < question.number_of_answers; i++) {
          const bubbleX = bubbleStartX + i * bubbleSpacing + bubbleRadius;
          bubbles.push({
            x: bubbleX,
            y: verticalCenter,
            answerIndex: i,
            letter: String.fromCharCode(65 + i),
          });
        }

        layouts.push({
          questionNumber: question.question_number,
          partId: question.part_id,
          bubbles,
        });
      });
    }
  }

  return layouts;
}
